import { Link } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Card, CardContent } from "@/components/ui/card";
import { Play, Video, Share2, BarChart3 } from "lucide-react";
import videoFile from "@/assets/Padel game  Drone footage  An incredible aerial perspective - F Seruya (1080p, h264).mp4";
import courtBg from "@/assets/court-bg.png";
import { useAuth } from "@/contexts/AuthContext";
import Layout from "@/components/ui/layout";
import tutorial1 from "@/assets/tutorial1.png";
import tutorial2 from "@/assets/tutorial2.png";
import tutorial3 from "@/assets/tutorial3.png";
import tutorial4 from "@/assets/tutorial4.png";
import { useEffect } from "react";

export const LandingRoute = () => {
  const { isAuthenticated, loading } = useAuth();

  if (loading) {
    return (
      <div className="flex items-center justify-center h-screen">
        Loading...
      </div>
    );
  }

  return isAuthenticated ? (
    <Layout>
      <Landing />
    </Layout>
  ) : (
    <Landing />
  );
};

const Landing = () => {
  const { isAuthenticated } = useAuth();

  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries, obs) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            entry.target.classList.add("fade-in-visible");

            // stop observing after animation
            obs.unobserve(entry.target);
          }
        });
      },
      { threshold: 0.15 }
    );

    document
      .querySelectorAll(".animate-on-scroll")
      .forEach((el) => observer.observe(el));

    return () => observer.disconnect();
  }, []);

  return (
    <div className="flex flex-col overflow-x-hidden hide-scrollbar">

      {/* ================= COURT BACKGROUND SECTION ================= */}
      <div
        className="relative bg-cover bg-center bg-no-repeat"
        style={{
          backgroundImage: `url(${courtBg})`,
        }}
      >
        {/* Dark overlay for readability */}
        <div className="absolute inset-0 bg-black/10 backdrop-blur-sm" />

        {/* Content wrapper */}
        <div className="relative z-10 flex flex-col">
          <main className=" flex-1 mx-auto max-w-6xl px-4 sm:px-6 py-10 sm:py-16">

            {/* HEADER */}
            <header className="flex flex-col gap-6 sm:flex-row sm:items-center sm:justify-between mb-12 animate-on-scroll fade-up">
              {/* Brand */}
              <div className="flex items-center gap-3 animate-on-scroll fade-left" style={{ transitionDelay: "0.1s" }}>
                <div className="w-11 h-11 rounded-md bg-white flex items-center justify-center shadow-sm">
                  <img
                    src="/favicon.ico"
                    alt="Court Replay Logo"
                    className="w-7 h-7 object-contain"
                  />
                </div>

                <div>
                  <h1 className="text-xl sm:text-2xl text-background font-bold">
                    Court Replay
                  </h1>
                  <p className="text-sm text-gray-300">
                    Highlights of your best plays
                  </p>
                </div>
              </div>

              {/* Nav */}
              <nav className="flex gap-3 w-full sm:w-auto animate-on-scroll fade-right" style={{ transitionDelay: "0.2s" }}>
                <Link to="/brands" className="flex-1 sm:flex-none">
                  <Button variant="outline" className="w-full">
                    Browse Courts
                  </Button>
                </Link>
                <Link to="/profile" className="flex-1 sm:flex-none">
                  <Button className="w-full">
                    My Profile
                  </Button>
                </Link>
              </nav>
            </header>

            {/* HERO */}
            <section className="grid grid-cols-1 md:grid-cols-2 gap-10 items-center mb-14">
              {/* Text */}
              <div className="text-center md:text-left animate-on-scroll fade-left">
                <Badge className="mb-4 " style={{ transitionDelay: "0.05s" }}>New • Highlights</Badge>

                <h2
                  className="text-3xl sm:text-4xl font-extrabold leading-tight mb-4 text-background animate-on-scroll fade-up"
                  style={{ transitionDelay: "0.15s" }}
                >
                  Rewatch the best moments.
                  <br className="hidden sm:block" />
                  Anytime. Anywhere.
                </h2>

                <p
                  className="text-gray-300 mb-6 max-w-xl mx-auto md:mx-0 animate-on-scroll fade-up"
                  style={{ transitionDelay: "0.25s" }}
                >
                  Court Replay automatically captures and surfaces match highlights
                  so you can relive your greatest plays, analyze performance, and
                  share the moments that matter.
                </p>

                <div
                  className="flex flex-col sm:flex-row gap-3 animate-on-scroll fade-up"
                  style={{ transitionDelay: "0.35s" }}
                >
                  <Link to="/brands" className="flex-1">
                    <Button size="lg" className="w-full">
                      Find Your Court
                    </Button>
                  </Link>
                  <Link to="/library" className="flex-1">
                    <Button size="lg" variant="outline" className="w-full">
                      My Library
                    </Button>
                  </Link>
                </div>
              </div>

              {/* Video */}
              <Card className="shadow-lg overflow-hidden animate-on-scroll fade-right">
                <div className="relative aspect-video">
                  <video
                    src={videoFile}
                    autoPlay
                    loop
                    muted
                    playsInline
                    className="absolute inset-0 w-full h-full object-cover"
                  />

                  <div className="absolute inset-0 bg-black/40" />

                  <div className="absolute inset-0 flex items-center justify-center animate-on-scroll pop-in" style={{ transitionDelay: "0.4s" }}>
                    <div className="bg-white/20 backdrop-blur-sm rounded-full p-4">
                      <Play className="w-10 h-10 sm:w-12 sm:h-12 text-white" />
                    </div>
                  </div>
                </div>

                <CardContent>
                  <h3 className="font-semibold text-lg mb-2 mt-2">
                    Instant Highlights
                  </h3>
                  <p className="text-sm text-muted-foreground">
                    Automatically trimmed replays, quality previews, and easy sharing tools.
                  </p>
                </CardContent>
              </Card>
            </section>

            {/* FEATURES */}
            <section className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 gap-6 mb-16">
              {[
                {
                  title: "Capture",
                  desc: "High quality recordings from every court session.",
                  icon: Video,
                },
                {
                  title: "Share",
                  desc: "Share clips with teammates and coaches instantly.",
                  icon: Share2,
                },
                {
                  title: "Analyze",
                  desc: "Slow-motion review and statistics to improve your game.",
                  icon: BarChart3,
                },
              ].map((item) => {
                const Icon = item.icon;

                return (
                  <Card
                    key={item.title}
                    className="hover:shadow-md transition-shadow duration-200 animate-on-scroll fade-up"
                    style={{ transitionDelay: `${0.1 + Math.random() * 0.2}s` }}
                  >
                    <CardContent className="p-6">
                      {/* Icon */}
                      <div className="w-12 h-12 rounded-lg bg-green-100 flex items-center justify-center mb-4">
                        <Icon className="w-6 h-6 text-green-600" />
                      </div>

                      {/* Title */}
                      <h4 className="font-semibold text-lg mb-2">
                        {item.title}
                      </h4>

                      {/* Description */}
                      <p className="text-sm text-muted-foreground">
                        {item.desc}
                      </p>
                    </CardContent>
                  </Card>
                );
              })}
            </section>

          </main>

          {/* GRADIENT FADE TO WHITE */}
          <div className="h-40 w-full bg-gradient-to-b from-transparent to-green-100" />
        </div>
      </div>


      {/* ================= WHITE SECTION ================= */}
      <div className="bg-green-100">

        <main className="mx-auto max-w-6xl px-4 sm:px-6">

          {/* TUTORIAL */}
          <section className="space-y-0">
            {[
              {
                title: "Play your game",
                text: "Play your game, and as soon as you get your highlight...",
                description:
                  "Enjoy your match as usual. Focus on playing your best game while the Court Replay system runs in the background, ready to capture the most exciting moments of your session.",
                image: tutorial1,
              },
              {
                title: "Press the highlight button",
                text: "Immediately press the button after the game highlight.",
                description:
                  "After an amazing rally, smash, or winning point, press the highlight button near the entrance of the court. This tells the system that a highlight just happened and marks the moment for recording.",
                image: tutorial2,
              },
              {
                title: "Automatic capture",
                text: "Within seconds, the last 30 seconds of your game will be captured!",
                description:
                  "Court Replay automatically records the most recent gameplay moments. When you press the highlight button, the system saves the previous 30 seconds so you never miss your best plays.",
                image: tutorial3,
              },
              {
                title: "Find your clips",
                text: "Look up your clips on this website using the same court you're currently playing.",
                description:
                  "After your game, simply visit this website and select the court you played on. Your recorded highlights will appear in the list where you can watch, download, or share them with friends.",
                image: tutorial4,
              },
            ].map((step, i) => {
              const reverse = i % 2 !== 0;

              return (
                <div
                  key={step.title}
                  className="flex items-center py-32"
                >
                  <div
                    className={`grid grid-cols-1 md:grid-cols-2 gap-10 md:gap-20 items-center w-full ${reverse ? "md:[&>*:first-child]:order-2" : ""
                      }`}
                  >
                    {/* TEXT SIDE */}
                    <div className={`max-w-2xl mx-auto md:mx-0`}>

                      {/* STEP LABEL */}
                      <div
                        className={`flex items-center gap-4 mb-8 animate-on-scroll pop-in
  ${reverse ? "justify-end" : "justify-start"}`}
                        style={{ transitionDelay: "0.15s" }}
                      >
                        <div className="w-20 h-16 rounded-full bg-green-600 text-white flex items-center justify-center text-3xl font-bold shadow-md">
                          {i + 1}
                        </div>

                        <span className="text-2xl sm:text-4xl font-semibold tracking-wider uppercase text-green-600">
                          Step {i + 1}
                        </span>
                      </div>

                      {/* HEADLINE */}
                      <h3
                        className={`text-5xl sm:text-6xl font-extrabold text-gray-900 leading-tight mb-6 animate-on-scroll ${reverse ? "fade-right" : "fade-left"
                          }`}
                        style={{ transitionDelay: "0.15s" }}
                      >
                        {step.title}
                      </h3>

                      {/* DESCRIPTION BLOCK */}
                      <div
                        className={`mt-6 pl-6 pr-6 border-green-600 animate-on-scroll ${reverse ? "fade-right md:border-r-4 md:pr-6 md:pl-0"
                          : "fade-left md:border-l-4 md:pl-6 md:pr-0"
                          }`}
                        style={{ transitionDelay: "0.3s" }}
                      >
                        <p className="text-xl text-gray-700 leading-relaxed mb-6 break-words">
                          {step.text}
                        </p>

                        <p className="text-lg text-gray-500 leading-relaxed">
                          {step.description}
                        </p>
                      </div>

                    </div>

                    {/* IMAGE SIDE */}
                    <div
                      className={`relative animate-on-scroll ${reverse ? "fade-left" : "fade-right"
                        }`}
                      style={{ transitionDelay: "0.45s" }}
                    >

                      <div className="absolute inset-0 md:-inset-6 bg-gradient-to-tr from-green-100 to-blue-100 rounded-3xl blur-3xl opacity-70" />

                      <div className="relative bg-white rounded-3xl shadow-2xl overflow-hidden border">
                        <img
                          src={step.image}
                          alt={step.title}
                          className="w-full h-full object-cover"
                        />
                      </div>

                    </div>
                  </div>
                </div>
              );
            })}
          </section>

          {/* LOGIN / REGISTER */}
          {!isAuthenticated && (
            <div className="w-full max-w-md mx-auto mt-6 text-center pb-24 animate-on-scroll fade-up">
              <p className="text-gray-800 text-lg sm:text-xl font-medium leading-relaxed mb-4">
                Access your recorded highlights or start capturing your best plays today.
              </p>

              <div
                className="flex flex-col sm:flex-row gap-4 animate-on-scroll pop-in"
                style={{ transitionDelay: "0.2s" }}
              >
                <Link to="/brands" className="flex-1">
                  <Button size="lg" className="w-full">
                    Login
                  </Button>
                </Link>

                <Link to="/register" className="flex-1">
                  <Button size="lg" variant="outline" className="w-full">
                    Register
                  </Button>
                </Link>
              </div>
            </div>
          )}

        </main>

        {/* FOOTER */}
        <footer className="text-center text-sm text-gray-500 pb-6 animate-on-scroll fade-up">
          © {new Date().getFullYear()} Court Replay — Relive your best plays
        </footer>

      </div>
    </div>
  );
};

export default Landing;